import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Autocomplete,
  TextInput,
  Textarea,
  Select,
  Grid,
  Col,
  Box,
  Flex,
  ActionIcon,
  useMantineColorScheme,
} from "@mantine/core";
import * as atlas from "azure-maps-control";
import LABELS_EN from "./LABELS_EN.json"; // Assuming you still need these
// Import other label files if needed (LABELS_FR, etc.)
import "@se/fonts/css/styles.css"; // Keep if necessary for specific fonts
import "@se/icons/css/styles.css"; // Keep if necessary for SE icons
import "azure-maps-control/dist/atlas.min.css";
import "./style.scss"; // Keep your custom styles
import { useAtom } from "jotai";
import { loaderAtom } from "../../utils/atoms/loaderAtom";
import { SeLoading } from "@se/web-ui-react";
import { locationAllDTOAtom } from "../../utils/atoms/locationAllDTOAtom";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { capitalizeFirstLetterAndSplitName, isAnyVersionCompleted } from "../../constants/helper";
import { mgaRegex } from "../../constants/helper";

// Constants
const latitudeDefault = 12.97375;
const longitudeDefault = 77.73439;
const mapKey = "nkw0wk8J4yUOJeQoQsBL42iT6q0Hx_VnV0I0JKX1diw"; // Consider moving to environment variables
const AZURE_MAPS_LANGUAGE = "en-US";
const addressCoordinatesURL = `https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0&subscription-key=`;
const addressTextURL = `https://atlas.microsoft.com/search/address/json?api-version=1.0&subscription-key=`;
const timezoneCoordinatesURL = `https://atlas.microsoft.com/timezone/byCoordinates/json?api-version=1.0&subscription-key=`;
const addressLatLongURL = `https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0&subscription-key=`;

const DEFAULT_LABELS = LABELS_EN;

const applyMapZoomCompensation = (mapElement) => {
  if (typeof window === "undefined" || !mapElement) return;

  let element = mapElement.parentElement;
  while (element && element !== document.documentElement) {
    const inheritedZoom = parseFloat(window.getComputedStyle(element).zoom);
    if (Number.isFinite(inheritedZoom) && inheritedZoom > 0 && Math.abs(inheritedZoom - 1) > 0.001) {
      mapElement.style.zoom = `${(1 / inheritedZoom) * 100}%`;
      mapElement.style.transformOrigin = "top left";
      return;
    }
    element = element.parentElement;
  }
};

// Mantine Theme (Optional: Customize if needed)

const LocationFinder = (props) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const {
    customLabels,
    locationData: initialLocationData = {},
    projectDTO: initialProjectData = {},
    locationDataCallback,
    mandatoryMsgDisplay,
    currencyList = [],
  } = props;
  const [, setLocationAllDTO] = useAtom(locationAllDTOAtom);
    const [projectDetailsedit, setProjectDetails] = useAtom(projectDetailsAtom);

  const isCompleted = isAnyVersionCompleted(
    projectDetailsedit?.designWithVersionsDTOList || []
  );
  // Build labels from i18n and allow optional overrides from customLabels
  const labels = useMemo(() => ({
    activeAddressSearch: t("locationFinder.activeAddressSearch"),
    manualAddressFilling: t("locationFinder.manualAddressFilling"),
    latitude: t("locationFinder.latitude"),
    longitude: t("locationFinder.longitude"),
    address: t("address"),
    city: t("city"),
    zipCode: t("zipCode"),
    stateProvince: t("stateProvince"),
    country: t("country"),
    timeZone: t("locationFinder.timeZone"),
    placeholderLatitude: t("locationFinder.placeholder.latitude"),
    placeholderlongitude: t("locationFinder.placeholder.longitude"),
    placeholderAddress: t("locationFinder.placeholder.address"),
    placeholderCity: t("locationFinder.placeholder.city"),
    placeholderZipCode: t("locationFinder.placeholder.zipCode"),
    placeholderStateProvince: t("locationFinder.placeholder.stateProvince"),
    placeholderCountry: t("locationFinder.placeholder.country"),
    placeholderTimeZone: t("locationFinder.placeholder.timeZone"),
    latitudeRequired: t("locationFinder.required.latitude"),
    longitudeRequired: t("locationFinder.required.longitude"),
    countryRequired: t("locationFinder.required.country"),
    timezoneRequired: t("locationFinder.required.timeZone"),
    ...(customLabels || {}),
  }), [t, customLabels]);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null); // To hold the map instance
  const dataSourceRef = useRef(null); // To hold the data source instance
  const pointRef = useRef(null); // To hold the point shape (SymbolLayer)
  const htmlMarkerRef = useRef(null); // To hold the draggable HtmlMarker
  const isDraggingRef = useRef(false);
  const originalViewRef = useRef({ center: [longitudeDefault, latitudeDefault], zoom: 12 });
  const [showReset, setShowReset] = useState(false);

  const [locationINData, setLocationINData] = useState({
    zipCode: initialLocationData.zipCode || "",
    timezone: initialLocationData.timezone || "",
    address: initialLocationData.address || "",
    country: initialLocationData.country || "",
    city: initialLocationData.city || "",
    stateProvince:
      initialLocationData.stateProvince || initialLocationData.state,
    latitude: initialLocationData.latitude || "",
    longitude: initialLocationData.longitude || "",
  });

  const [projectData, setProjectData] = useState({
    projectName: initialProjectData.projectName || "",
    projectDescription: initialProjectData.projectDescription || "",
    customerName: initialProjectData.customerName || "",
    currency: initialProjectData.currency || "",
    currencyCode: initialProjectData.currencyCode || "",
    dateFormat: initialProjectData.dateFormat || "YYYY/MM/DD",
    numberFormat: initialProjectData.numberFormat || "1,234,567.89",
    projectType: initialProjectData.projectType || "ANSI", // Default if not provided
  });

  // Control the search input of the currency Select so selected label appears immediately
  const [currencySearch, setCurrencySearch] = useState("");

  const [activeSearchText, setActiveSearchText] = useState("");
  const [activeSearchResult, setActiveSearchResult] = useState([]);
  const [loading, setLoading] = useAtom(loaderAtom);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading for autocomplete
  const initialDataPublishedRef = useRef(false);

  const [errors, setErrors] = useState({
    projectName: "",
    customerName: "",
    projectDescription: "", // Added for consistency
    dateFormat: "",
    numberFormat: "",
    latitude: "",
    longitude: "",
    country: "",
    currency: "",
    zipCode: "",
    stateProvince: "",
  });

  // --- Utility Functions ---

  const runCallback = useCallback(
    (data) => {
      if (locationDataCallback) {
        locationDataCallback(data);
      }
    },
    [locationDataCallback]
  );

  useEffect(() => {
    setLocationAllDTO((prev) => {
      const safePrev = typeof prev === "object" && prev !== null ? prev : {};
      return { ...safePrev, projectData, locationINData };
    });
  }, [projectData, locationINData, setLocationAllDTO]);

  useEffect(() => {
    if (initialDataPublishedRef.current) return;
    initialDataPublishedRef.current = true;
    runCallback({ locationINData, projectData });
  }, [runCallback]);

  // Recompute and surface field errors whenever inputs or the mandatory flag change
  useEffect(() => {
    const newErrors = getValidationErrors({ projectData, locationINData, mandatoryMsgDisplay, labels });
    setErrors(newErrors);
  }, [projectData, locationINData, mandatoryMsgDisplay, labels]);

  const validateInput = () => {
    let isValid = true;
    const newErrors = {
      projectName: projectData.projectName ? "" : t("projectDetails.projectName.required"),
      customerName: projectData.customerName ? "" : t("projectDetails.customerName.required"),
      projectDescription: errors.projectDescription,
      dateFormat: projectData.dateFormat ? "" : "Date format is required",
      numberFormat: projectData.numberFormat ? "" : "Number format is required",
      latitude: locationINData.latitude ? "" : t("locationFinder.required.latitude"),
      longitude: locationINData.longitude ? "" : t("locationFinder.required.longitude"),
      country: locationINData.country ? "" : t("locationFinder.required.country"),
      currency: projectData.currency ? "" : t("projectDetails.currency.required"),
    };

    setErrors(newErrors);
    isValid = !Object.values(newErrors).some((error) => error !== ""); // Check if any error exists
    return isValid;
  };

  // --- Map Functions ---

  const updateMapPosition = useCallback((lat, lon) => {
    if (mapRef.current) {
      // Map can be a partial mock in unit tests. Guard missing methods.
      if (typeof mapRef.current.setCamera === "function") {
        mapRef.current.setCamera({
          center: [lon, lat],
          zoom: 12,
        });
      }
      if (pointRef.current && dataSourceRef.current) {
        // Check if point needs to be created
        if (!pointRef.current) {
          pointRef.current = new atlas.Shape(new atlas.data.Point([lon, lat]));
          dataSourceRef.current.add(pointRef.current);
        } else {
          // Update existing point coordinates
          pointRef.current.setCoordinates([lon, lat]);
        }
      }
    }
  }, []);

  const initializeMap = useCallback(
    (lat, lon) => {
      if (mapContainerRef.current && !mapRef.current) {
        applyMapZoomCompensation(mapContainerRef.current);

        // Ensure map is initialized only once
        const map = new atlas.Map(mapContainerRef.current.id, {
          disableTelemetry: true,
          authOptions: {
            authType: "subscriptionKey",
            subscriptionKey: mapKey,
          },
          language: AZURE_MAPS_LANGUAGE,
          style: colorScheme === "dark" ? "night" : "light",
          color: "DodgerBlue",
          text: "10",
          x: 2932,
          y: 1899,
          view: "Auto",
          position: [0, 0],
          center: [lon, lat],
          zoom: 12,
        });

        mapRef.current = map; // Store map instance
        originalViewRef.current = { center: [lon, lat], zoom: 12 };

        // In unit tests we mock Azure Maps. Some mocks/bundler interop can omit `map.events`.
        // Fall back to no-op handlers so the component can mount.
        const events = map?.events || {
          add: () => {},
          remove: () => {},
        };

        events.add("ready", () => {
          // Add controls
          map.controls.add(new atlas.control.ZoomControl(), {
            position: "top-right",
          });

          // Hide unnecessary elements
          const shortcuts = document.getElementById("atlas-map-shortcuts");
          const state = document.getElementById("atlas-map-state");
          if (shortcuts) shortcuts.style.visibility = "hidden";
          if (state) state.style.visibility = "hidden";

          // Setup data source and layer for the marker
          const ds = new atlas.source.DataSource();
          map.sources.add(ds);
          dataSourceRef.current = ds; // Store data source instance

          pointRef.current = new atlas.Shape(new atlas.data.Point([lon, lat]));
          ds.add(pointRef.current); // Add initial point

          map.layers.add(new atlas.layer.SymbolLayer(ds, null));

          // Add a draggable HtmlMarker for easier repositioning
          htmlMarkerRef.current = new atlas.HtmlMarker({
            position: [lon, lat],
            draggable: !isCompleted,
          });
          map.markers.add(htmlMarkerRef.current);

          // Add click event listener
          if (!isCompleted) {
            events.add("click", handleMapClick);
          }

          // Handle drag end to update location
          if (!isCompleted && htmlMarkerRef.current) {
            // Hide the SymbolLayer point while dragging the marker
            events.add("dragstart", htmlMarkerRef.current, () => {
              isDraggingRef.current = true;
              try {
                if (pointRef.current && dataSourceRef.current) {
                  dataSourceRef.current.remove(pointRef.current);
                }
              } catch (e) {}
            });
            events.add("dragend", htmlMarkerRef.current, () => {
              const pos = htmlMarkerRef.current.getOptions().position;
              if (!pos) return;
              const [lonNew, latNew] = pos;
              const newLocationData = {
                ...locationINData,
                latitude: Number(latNew).toFixed(5),
                longitude: Number(lonNew).toFixed(5),
              };
              // Keep SymbolLayer point in sync
              try {
                if (dataSourceRef.current) {
                  // Recreate or re-add point and set its position
                  if (!pointRef.current) {
                    pointRef.current = new atlas.Shape(new atlas.data.Point([lonNew, latNew]));
                  } else {
                    pointRef.current.setCoordinates([lonNew, latNew]);
                  }
                  dataSourceRef.current.add(pointRef.current);
                }
              } catch (e) {}
              isDraggingRef.current = false;
              searchTimezoneByLatLong(latNew, lonNew, newLocationData);
              searchAddressByLatLong(latNew, lonNew, newLocationData);
              runCallback({ locationINData: newLocationData, projectData });
            });
          }

          // Show reset when camera moves away from original view
          map.events.add("moveend", () => {
            try {
              const cam = map.getCamera();
              const [origLon, origLat] = originalViewRef.current.center;
              const distLon = Math.abs(cam.center[0] - origLon);
              const distLat = Math.abs(cam.center[1] - origLat);
              const zoomChanged = Math.abs((cam.zoom || 0) - (originalViewRef.current.zoom || 0)) > 0.01;
              const centerMoved = distLon > 0.0005 || distLat > 0.0005; // ~50m threshold
              setShowReset(zoomChanged || centerMoved);
            } catch (e) {
              // ignore
            }
          });

          // Initial resize after map is ready
          map.resize();
        });
      } else if (mapRef.current) {
        // If map already initialized, just update position
        updateMapPosition(lat, lon);
        originalViewRef.current = { center: [lon, lat], zoom: 12 };
      }
    },
    [updateMapPosition]
  ); // Add updateMapPosition to dependencies

  const handleMapClick = useCallback(
    (e) => {
      if (isCompleted) return;
      if (e && e.position) {
        const [lon, lat] = e.position;
        const newLocationData = {
          ...locationINData,
          latitude: lat.toFixed(5), // Adjust precision as needed
          longitude: lon.toFixed(5),
        };
        // Update both marker types
        if (pointRef.current && dataSourceRef.current) {
          pointRef.current.setCoordinates([lon, lat]); // Update the marker's position
        }
        if (htmlMarkerRef.current) {
          htmlMarkerRef.current.setOptions({ position: [lon, lat] });
        }
        // setLocationINData(newLocationData);
        // updateMapPosition(lat, lon); // Update marker position visually
        searchTimezoneByLatLong(lat, lon, newLocationData); // Pass updated data
        searchAddressByLatLong(lat, lon, newLocationData); // Pass updated data
        runCallback({ locationINData: newLocationData, projectData });
      }
    },
    [locationINData, runCallback, isCompleted]
  ); // Include dependencies

  // --- API Call Functions ---

  const searchTimezoneByLatLong = useCallback(
    async (lat, long, currentLocData) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${timezoneCoordinatesURL}${mapKey}&query=${lat},${long}`
        );
        if (!response.ok) throw new Error(response.statusText);
        const json = await response.json();
        const timezone = json?.TimeZones?.[0]?.Id || "";
        const updatedData = { ...currentLocData, timezone };
        setLocationINData(updatedData);
        runCallback({ locationINData: updatedData, projectData });
      } catch (error) {
        console.error("Error fetching timezone:", error);
        const updatedData = { ...currentLocData, timezone: "" };
        setLocationINData(updatedData); // Clear timezone on error
        runCallback({ locationINData: updatedData, projectData });
      } finally {
        setLoading(false);
      }
    },
    [projectData, runCallback]
  );

  const searchAddressByLatLong = useCallback(
    async (lat, long, currentLocData) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${addressLatLongURL}${mapKey}&query=${lat},${long}&language=${AZURE_MAPS_LANGUAGE}`
        );
        if (!response.ok) throw new Error(response.statusText);
        const json = await response.json();

        if (json.addresses && json.addresses.length > 0) {
          const addr = json.addresses[0].address;
          const streetAddress =
            addr.streetNameAndNumber ||
            addr.freeformAddress ||
            (addr.streetName && addr.streetNumber
              ? `${addr.streetNumber}, ${addr.streetName}`
              : addr.streetName) ||
            `${addr.municipality || ""}, ${
              addr.countrySecondarySubdivision || ""
            }, ${addr.countrySubdivision || ""}`.trim();
          const freeformAddress = addr.freeformAddress || streetAddress;

          const updatedData = {
            ...currentLocData,
            address: streetAddress,
            country: addr.country || "",
            city: addr.municipality || addr.countrySecondarySubdivision || "",
            zipCode: addr.postalCode || "",
            stateProvince: addr.countrySubdivision || "",
          };
          setLocationINData(updatedData);
          setActiveSearchText(freeformAddress); // Update search text field
          runCallback({ locationINData: updatedData, projectData });
        } else {
          // Clear fields if no address found
          const clearedData = {
            ...currentLocData,
            address: "",
            country: "",
            city: "",
            zipCode: "",
            stateProvince: "",
          };
          setLocationINData(clearedData);
          setActiveSearchText(""); // Clear search text
          runCallback({ locationINData: clearedData, projectData });
        }
      } catch (error) {
        console.error("Error fetching address by lat/long:", error);
        // Optionally clear fields on error too
        const errorData = {
          ...currentLocData,
          address: "",
          country: "",
          city: "",
          zipCode: "",
          stateProvince: "",
        };
        setLocationINData(errorData);
        setActiveSearchText("");
        runCallback({ locationINData: errorData, projectData });
      } finally {
        setLoading(false);
      }
    },
    [projectData, runCallback]
  );

  const searchAddressByFreeText = useCallback(async (query) => {
    if (query.length < 4) {
      setActiveSearchResult([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await fetch(
        `${addressTextURL}${mapKey}&query=${encodeURIComponent(query)}&language=${AZURE_MAPS_LANGUAGE}`
      );
      if (!response.ok) throw new Error(response.statusText);
      const json = await response.json();
      const results =
        json.results?.map((data) => ({
          value: data.address.freeformAddress, // Use freeformAddress as the value for Autocomplete
          label: data.address.freeformAddress, // And as the label
          fullDetails: data.address,
          position: data.position,
        })) || [];
      setActiveSearchResult(results);
    } catch (error) {
      console.error("Error searching address by text:", error);
      setActiveSearchResult([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // --- Event Handlers ---

  const handleSearchChange = (value) => {
    if (isCompleted) return;
    const sanitizedValue = value.replace(/\s+/g, " ").trimStart();
    setActiveSearchText(sanitizedValue);
    searchAddressByFreeText(sanitizedValue); // Trigger search on change
    if (sanitizedValue === "") {
      // Clear location data if search is cleared
      const clearedLocationData = {
        zipCode: "",
        timezone: "",
        address: "",
        country: "",
        city: "",
        stateProvince: "",
        latitude: "",
        longitude: "",
      };
      setLocationINData(clearedLocationData);
      if (mapRef.current) {
        // Reset map if cleared
        updateMapPosition(latitudeDefault, longitudeDefault);
      }
    }
  };

  const handleSearchSelect = ({ value }) => {
    if (isCompleted) return;
    const selectedResult = activeSearchResult.find(
      (item) => item.value === value
    );
    if (selectedResult || true) {
      setLoading(true);
      const { fullDetails, position } = selectedResult;
      const lat = position.lat;
      const lon = position.lon;

      const newLocationData = {
        ...locationINData,
        address:
          fullDetails.streetNameAndNumber ||
          fullDetails.freeformAddress ||
          (fullDetails.streetName && fullDetails.streetNumber
            ? `${fullDetails.streetNumber}, ${fullDetails.streetName}`
            : fullDetails.streetName) ||
          `${fullDetails.municipality || ""}, ${
            fullDetails.countrySecondarySubdivision || ""
          }, ${fullDetails.countrySubdivision || ""}`.trim(),
        country: fullDetails.country || "",
        city:
          fullDetails.municipality ||
          fullDetails.countrySecondarySubdivision ||
          "",
        zipCode: fullDetails.postalCode || "",
        stateProvince: fullDetails.countrySubdivision || "",
        latitude: lat?.toString() || "",
        longitude: lon?.toString() || "",
      };

      setLocationINData(newLocationData);
      setActiveSearchText(value); // Keep selected value in input
      setActiveSearchResult([]); // Clear dropdown results

          if (lat !== undefined && lon !== undefined) {
            // Ensure only one pointer exists and move it to the new position
            try {
              if (dataSourceRef.current) {
                dataSourceRef.current.clear();
              }
              if (!pointRef.current) {
                pointRef.current = new atlas.Shape(new atlas.data.Point([lon, lat]));
              } else {
                pointRef.current.setCoordinates([lon, lat]);
              }
              if (dataSourceRef.current) {
                dataSourceRef.current.add(pointRef.current);
              }
              if (htmlMarkerRef.current) {
                htmlMarkerRef.current.setOptions({ position: [lon, lat] });
              }
            } catch (e) {}
            updateMapPosition(lat, lon);
            searchTimezoneByLatLong(lat, lon, newLocationData); // Pass updated data
      } else {
        // Handle case where position might be missing (though unlikely for search results)
        searchTimezoneByLatLong(
          locationINData.latitude,
          locationINData.longitude,
          newLocationData
        );
      }
      runCallback({ locationINData: newLocationData, projectData }); // Callback after state update
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const validInputRegex = /^[a-zA-Z0-9 _-]*$/; // Allow letters, numbers, space, underscore, hyphen
    const MAX_LENGTHS = {
      projectName: 100,
      customerName: 30,
      projectDescription: 200,
      zipCode: 9, // Example max length
      address: 200,
      city: 100,
      stateProvince: 100,
      country: 100,
    };

    let sanitizedValue = value;
    let isProjectField = [
      "projectName",
      "customerName",
      "projectDescription",
      "currency",
      "projectType",
    ].includes(name);
    let isLocationField = !isProjectField;

    // Basic sanitization for most fields (allow specific chars)
    if (
      name !== "latitude" &&
      name !== "longitude" &&
      name !== "currency"
    ) {
      sanitizedValue = value
        .split("")
        .filter((char) => validInputRegex.test(char))
        .join("");
    } else if (name === "latitude" || name === "longitude") {
      // Allow numbers, decimal point, and negative sign for lat/lon
      sanitizedValue = value.replace(/[^0-9.-]/g, "");
    }

    // Apply max length
    if (name in MAX_LENGTHS && sanitizedValue.length > MAX_LENGTHS[name]) {
      if (isProjectField) {
        setErrors((prev) => ({
          ...prev,
          [name]: `${capitalizeFirstLetterAndSplitName(name)} must not exceed ${MAX_LENGTHS[name]} characters.`,
        }));
      }
      // Don't update state if max length exceeded for project fields
      // For location fields, we might just truncate or let the input handlemaxlength
      if (isProjectField) return;
      sanitizedValue = sanitizedValue.substring(0, MAX_LENGTHS[name]); // Truncate for location fields if needed
    } else if (isProjectField) {
      // Clear error if within limits
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

  if (isProjectField) {
      let currencyCode = projectData.currencyCode;
      if (name === "currency") {
        const selectedOption = e.target.options[e.target.selectedIndex];
        currencyCode = selectedOption
          ? selectedOption.getAttribute("data-code")
          : "";
      }
      const newProjectData = {
        ...projectData,
        [name]: sanitizedValue,
        ...(name === "currency" && { currencyCode }),
      };
      setProjectData(newProjectData);
      // Re-validate and set errors after project field change
      const updatedErrors = getValidationErrors({ projectData: newProjectData, locationINData, mandatoryMsgDisplay, labels });
      setErrors(updatedErrors);
      runCallback({ locationINData, projectData: newProjectData });
    } else {
      const newLocationData = { ...locationINData, [name]: sanitizedValue };
      setLocationINData(newLocationData);
      // Re-validate and set errors after location field change
      const updatedErrors = getValidationErrors({ projectData, locationINData: newLocationData, mandatoryMsgDisplay, labels });
      setErrors(updatedErrors);
      runCallback({ locationINData: newLocationData, projectData });
    }
  };

  const handleLatLonBlur = () => {
    if (isCompleted) return;
    if (locationINData.latitude && locationINData.longitude) {
      // Validate before searching
      const lat = parseFloat(locationINData.latitude);
      const lon = parseFloat(locationINData.longitude);
      if (!isNaN(lat) && !isNaN(lon)) {
        setLoading(true);
        // Ensure only one pointer: reset datasource and sync both markers
        try {
          if (dataSourceRef.current) {
            dataSourceRef.current.clear();
          }
          if (!pointRef.current) {
            pointRef.current = new atlas.Shape(new atlas.data.Point([lon, lat]));
          } else {
            pointRef.current.setCoordinates([lon, lat]);
          }
          if (dataSourceRef.current) {
            dataSourceRef.current.add(pointRef.current);
          }
          if (htmlMarkerRef.current) {
            htmlMarkerRef.current.setOptions({ position: [lon, lat] });
          }
        } catch (e) {}
        updateMapPosition(lat, lon);
        searchTimezoneByLatLong(lat, lon, locationINData);
        searchAddressByLatLong(lat, lon, locationINData);
        // setLoading handled within the async search functions
      }
    } else {
      // Handle invalid input if needed, errors are set by validateInput
      console.log("Invalid Lat/Lon input on blur");
    }
  };

  // --- Effects ---

  // Initialize map on mount
  useEffect(() => {
    const lat =
      locationINData.latitude !== ""
        ? Number(locationINData.latitude)
        : latitudeDefault;
    const lon =
      locationINData.longitude !== ""
        ? Number(locationINData.longitude)
        : longitudeDefault;
    initializeMap(lat, lon);

    // Set initial search text if address exists
    if (initialLocationData.address) {
      let initialSearchText = initialLocationData.address;
      // Potentially use freeformAddress if available and preferred
      setActiveSearchText(initialSearchText);
    }

    // Cleanup map instance on unmount
    return () => {
      if (mapRef.current) {
        // In unit tests, mapRef can be a partial mock. Guard to avoid crashing cleanup.
        if (typeof mapRef.current.dispose === "function") {
          mapRef.current.dispose();
        }
        mapRef.current = null;
        dataSourceRef.current = null;
        pointRef.current = null;
        htmlMarkerRef.current = null;
      }
    };
  }, [initializeMap, initialLocationData.address]); // Run only once on mount

  return (
    <>
      <SeLoading loading={loading} />
      <div p="md">
        <Grid>
          <Col span={4}>
            {/* Project Details Section */}
            <Box mb="lg">
              <h5>Project And Site Details</h5>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="projectName">
                    Project Name
                    <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    id="projectName"
                    name="projectName"
                    value={projectData.projectName}
                    placeholder="Enter Project Name"
                    onChange={handleInputChange}
                    error={errors.projectName}
                    required
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="customerName">
                    Customer Name
                    <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    id="customerName"
                    name="customerName"
                    value={projectData.customerName}
                    placeholder="Enter Customer Name"
                    onChange={handleInputChange}
                    error={errors.customerName}
                    required
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="projectDescription">
                    Project Description
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <Textarea
                    id="projectDescription"
                    name="projectDescription"
                    value={projectData.projectDescription}
                    placeholder="Enter project description"
                    onChange={handleInputChange}
                    error={errors.projectDescription}
                    maxLength={200}
                    autosize
                    minRows={1}
                    maxRows={5}
                    description={`${projectData?.projectDescription?.length}/200 characters`}
                    className="project-description-textarea"
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="currency">
                    Currency
                    <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <Select
                    id="currency"
                    name="currency"
                    value={projectData.currency}
                    onChange={(value) => {
                      if (isCompleted) return;
                      const selectedCurrency = currencyList.find(
                        (c) => c.name === value
                      );
                      const currencyCode = selectedCurrency
                        ? selectedCurrency.symbol
                        : "";
                      const newProjectData = {
                        ...projectData,
                        currency: value || "",
                        currencyCode: currencyCode,
                      };
                      setProjectData(newProjectData);
                      runCallback({ locationINData, projectData: newProjectData });
                      // set search text to the selected label so the Select shows it immediately
                      setCurrencySearch(
                        selectedCurrency
                          ? `${selectedCurrency.name} (${selectedCurrency.symbol})`
                          : ""
                      );
                    }}
                    searchValue={currencySearch}
                    onSearchChange={(val) => setCurrencySearch(val)}
                    data={
                      currencyList
                        ?.filter((c) => c.status !== "D")
                        .map((currencyData) => ({
                          value: currencyData.name,
                          label: `${currencyData.name} (${currencyData.symbol})`,
                        })) || []
                    }
                    placeholder="Select currency"
                    error={errors.currency}
                    required
                    searchable
                    withinPortal
                    disabled={isCompleted}
                  />
                </Box>
              </Flex>
            </Box>

            {/* Preferences Section */}
            <Box mb="lg">
              <h5>PREFERENCES</h5>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="dateFormat">
                    Date Format
                    <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <Select
                    id="dateFormat"
                    name="dateFormat"
                    value={projectData.dateFormat}
                    onChange={(value) => {
                      const newProjectData = {
                        ...projectData,
                        dateFormat: value || "",
                      };
                      setProjectData(newProjectData);
                      runCallback({ locationINData, projectData: newProjectData });
                    }}
                    data={[
                      { value: "YYYY/MM/DD", label: "YYYY/MM/DD" },
                      { value: "DD/MM/YYYY", label: "DD/MM/YYYY" },
                      { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
                      { value: "DD MMM YYYY", label: "DD MMM YYYY" },
                    ]}
                    error={errors.dateFormat}
                    required
                    withinPortal
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="numberFormat">
                    Number Format
                    <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <Select
                    id="numberFormat"
                    name="numberFormat"
                    value={projectData.numberFormat}
                    onChange={(value) => {
                      const newProjectData = {
                        ...projectData,
                        numberFormat: value || "",
                      };
                      setProjectData(newProjectData);
                      runCallback({ locationINData, projectData: newProjectData });
                    }}
                    data={[
                      { value: "1,234,567.89", label: "1,234,567.89" },
                      { value: "1.234.567,89", label: "1.234.567,89" },
                      { value: "1 234 567,89", label: "1 234 567,89" },
                      { value: "12,34,567.89", label: "12,34,567.89" },
                    ]}
                    error={errors.numberFormat}
                    required
                    withinPortal
                  />
                </Box>
              </Flex>
            </Box>

            {/* Location Details Section */}
            <Box>
              <h5>LOCATION DETAILS</h5>
              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="activeAddressSearch">
                    {labels.activeAddressSearch}
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <Autocomplete
                    name="activeAddressSearch"
                    placeholder="Search for location..."
                    value={activeSearchText}
                    onChange={handleSearchChange}
                    onItemSubmit={handleSearchSelect} // Use onOptionSubmit for selection
                    data={activeSearchResult.map((item) => item.value)} // Pass only values
                    limit={15} // Limit dropdown results
                    filter={() => true}
                    id="combo-box-location"
                    withinPortal
                    maxDropdownHeight={200}
                    // Consider adding debouncing for searchAddressByFreeText call in handleSearchChange
                    disabled={isCompleted}
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="latitude">
                    {labels.latitude}
                    <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    type="number"
                    id="latitude"
                    name="latitude"
                    value={locationINData.latitude}
                    placeholder={labels.placeholderLatitude}
                    onChange={handleInputChange}
                    onBlur={handleLatLonBlur}
                    disabled={isCompleted}
                    error={errors.latitude}
                    required
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="longitude">
                    {labels.longitude}
                    <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    type="number"
                    id="longitude"
                    name="longitude"
                    value={locationINData?.longitude}
                    placeholder={labels.placeholderlongitude}
                    onChange={handleInputChange}
                    onBlur={handleLatLonBlur}
                    disabled={isCompleted}
                    error={errors.longitude}
                    required
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="address">{labels.address}</label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    id="address"
                    name="address"
                    value={locationINData.address}
                    placeholder={labels.placeholderAddress}
                    onChange={handleInputChange}
                    disabled={isCompleted}
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="city">{labels.city}</label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    id="city"
                    name="city"
                    value={locationINData.city}
                    placeholder={labels.placeholderCity}
                    onChange={handleInputChange}
                    disabled={isCompleted}
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="zipCode">{labels.zipCode}
                     <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    id="zipCode"
                    name="zipCode"
                    value={locationINData.zipCode}
                    placeholder={labels.placeholderZipCode}
                    onChange={handleInputChange}
                     disabled={isCompleted}
                     error={errors.zipCode}
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="stateProvince">{labels.stateProvince}
                     {/* <span className="se-mandatory ps-1 pt-0">*</span> */}
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    id="stateProvince"
                    name="stateProvince"
                    value={locationINData.stateProvince || locationINData.state}
                    placeholder={labels.placeholderStateProvince}
                    onChange={handleInputChange}
                    disabled={isCompleted}
                    //  error={
                    //   errors?.stateProvince ||
                    //   (mandatoryMsgDisplay && !locationINData?.stateProvince
                    //     ? "State / Province is required"
                    //     : "")}
                  />
                </Box>
              </Flex>

              <Flex align="center" justify="space-between" mb="sm">
                <Box style={{ width: "30%" }}>
                  <label htmlFor="country">
                    {labels.country}
                    <span className="se-mandatory ps-1 pt-0">*</span>
                  </label>
                </Box>
                <Box style={{ width: "70%" }}>
                  <TextInput
                    id="country"
                    name="country"
                    value={locationINData.country}
                    placeholder={labels.placeholderCountry}
                    onChange={handleInputChange}
                    disabled={isCompleted}
                    error={errors.country}
                    required
                  />
                </Box>
              </Flex>
            </Box>
          </Col>

          {/* Map Section */}
          <Col span={8}>
            <div style={{ position: "relative", height: "100%", width: "100%" }}>
              <div
                id="embMapContainer"
                ref={mapContainerRef}
                style={{ height: "100%", width: "100%" }}
              />
              {showReset && (
                <ActionIcon
                  variant="filled"
                  size="lg"
                  onClick={() => {
                    try {
                      const map = mapRef.current;
                      if (!map) return;
                      // Reset view to the current marker position, not the initial default
                      let center = null;
                      if (htmlMarkerRef.current && htmlMarkerRef.current.getOptions().position) {
                        const pos = htmlMarkerRef.current.getOptions().position;
                        center = [pos[0], pos[1]];
                      } else if (pointRef.current) {
                        const coords = pointRef.current.getCoordinates();
                        center = [coords[0], coords[1]];
                      } else {
                        center = originalViewRef.current.center;
                      }
                      const currentZoom = map.getCamera().zoom || originalViewRef.current.zoom || 12;
                      map.setCamera({ center, zoom: currentZoom });
                      setShowReset(false);
                    } catch (e) {}
                  }}
                  aria-label="Reset map to original view"
                  style={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    zIndex: 10,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <polyline points="1 20 1 14 7 14"></polyline>
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"></path>
                  </svg>
                </ActionIcon>
              )}
            </div>
          </Col>
        </Grid>
      </div>
    </>
  );
};

// Centralized validation helper to compute field errors consistently
const getValidationErrors = ({ projectData, locationINData, mandatoryMsgDisplay, labels }) => {
  const errs = {
    projectName: !projectData?.projectName && mandatoryMsgDisplay ? "Project name is required" : "",
    customerName: !projectData?.customerName && mandatoryMsgDisplay ? "Customer name is required" : "",
    projectDescription:
      projectData?.projectDescription && projectData.projectDescription.length > 200
        ? "Project description must not exceed 200 characters"
        : "",
    dateFormat: !projectData?.dateFormat && mandatoryMsgDisplay ? "Date format is required" : "",
    numberFormat: !projectData?.numberFormat && mandatoryMsgDisplay ? "Number format is required" : "",
    currency: !projectData?.currency && mandatoryMsgDisplay ? "Currency is required" : "",
    latitude: !locationINData?.latitude && mandatoryMsgDisplay ? labels.latitudeRequired : "",
    longitude: !locationINData?.longitude && mandatoryMsgDisplay ? labels.longitudeRequired : "",
    country: !locationINData?.country && mandatoryMsgDisplay ? labels.countryRequired : "",
    zipCode: !locationINData?.zipCode && mandatoryMsgDisplay ? "Zip Code is required" : "",
    stateProvince: "", // Not mandatory currently; add rule if needed
  };

  if (mandatoryMsgDisplay) {
    const projectName = (projectData?.projectName ?? "").trim();
    // If present but too short, show a dedicated message (must be > 5 chars)
    if (!errs.projectName && projectName && projectName.length < 5) {
      errs.projectName = "Project name has to be at least 5 characters";
    }
  }

  // Space rules for names using mgaRegex if available
  if (projectData?.projectName && typeof mgaRegex?.leadingTrailingInternalSpacesRegex?.test === "function") {
    if (!mgaRegex.leadingTrailingInternalSpacesRegex.test(projectData.projectName)) {
      errs.projectName = "Project names must not have leading, trailing, or consecutive internal spaces";
    }
  }
  if (projectData?.customerName && typeof mgaRegex?.leadingTrailingInternalSpacesRegex?.test === "function") {
    if (!mgaRegex.leadingTrailingInternalSpacesRegex.test(projectData.customerName)) {
      errs.customerName = "Customer name must not have leading, trailing, or consecutive internal spaces";
    }
  }

  return errs;
};

// --- Static Methods (if needed, though less common in functional components) ---
LocationFinder.getLabelsForLanguage = (language) => {
  // ... (keep original logic)
  return LABELS_EN; // Placeholder
};

LocationFinder.getValidationLabelsForLanguage = (language) => {
  // ... (keep original logic)
  return {}; // Placeholder
};

// Validation logic can be moved into the component or kept separate
LocationFinder.validateLocation = (locationData, projectData = {}, locationProject = {}, mandatoryMsgDisplay = true) => {
  // Prefer provided projectData, fall back to locationProject if fields missing
  const mergedProject = {
    projectName: projectData?.projectName || locationProject?.projectName,
    customerName: projectData?.customerName || locationProject?.customerName,
    currency: projectData?.currency || locationProject?.currency,
    projectDescription: projectData?.projectDescription || locationProject?.projectDescription,
    dateFormat: projectData?.dateFormat || locationProject?.dateFormat,
    numberFormat: projectData?.numberFormat || locationProject?.numberFormat,
  };
  const labels = DEFAULT_LABELS || {};
  const newErrors = getValidationErrors({ projectData: mergedProject, locationINData: locationData, mandatoryMsgDisplay, labels });
  // Return true if there ARE errors (i.e., validation failed), else false
  return Object.values(newErrors).filter(Boolean).length > 0;
};

export default LocationFinder;
